import React from 'react';
import { createPortal } from 'react-dom';
import { Z } from '../../styles/zLayers';

interface TendModalProps {
  title: React.ReactNode;
  onClose: () => void;
  /** Panel width cap; the panel is min(width, 100%). */
  width?: number;
  /** Optional mono subtitle under the title. */
  subtitle?: React.ReactNode;
  /** Stacking rung (see styles/zLayers). Defaults to the editor-modal tier so
   *  the shell clears an item modal it opens from; pass a higher rung only
   *  when a consumer has to sit above a menu or picker. */
  zIndex?: number;
  children: React.ReactNode;
}

/**
 * The TEND modal shell: body portal (escapes every backdrop-filter
 * containing block — pills, panels, stacks), frost veil (never a black
 * scrim — the world softens, it doesn't dim), centered ink card rising
 * on the veil's clock, editorial title + close, scrolling body. Veil
 * mousedown closes; inner clicks don't.
 *
 * A body portal is a sibling of every other body portal, so the shell
 * must out-stack whatever opened it by number: `.tend-modal-veil`'s own
 * z (10000) is below the item modals (ITEM_MODAL), which is how View
 * Prompt opened underneath the tended card's frost.
 */
const TendModal: React.FC<TendModalProps> = ({
  title, onClose, width = 480, subtitle, zIndex = Z.EDITOR_MODAL, children,
}) => {
  return createPortal(
    <div
      className="tend-modal-veil"
      style={{ zIndex }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="tend-ink tend-modal-card"
        style={{
          width: `min(${width}px, 100%)`,
          maxHeight: '85vh',
          borderRadius: 20,
          padding: 18,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <span
              style={{
                fontFamily: "'Inter Tight', sans-serif",
                fontWeight: 700,
                fontSize: 17,
                letterSpacing: '-.01em',
                color: 'var(--text)',
              }}
            >
              {title}
            </span>
            {subtitle && (
              <div className="tend-micro" style={{ textTransform: 'none', letterSpacing: 0.4, marginTop: 3 }}>
                {subtitle}
              </div>
            )}
          </div>
          <button
            type="button"
            className="tend-btn tend-btn--icon tend-btn--icon-sm"
            aria-label="Close"
            onClick={onClose}
            style={{ flexShrink: 0 }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
        <div
          className="tend-scroll"
          style={{
            overflowY: 'auto',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            // Net-zero gutter so selection rings (2px box-shadows, painted
            // outside their row's box) aren't clipped by this scroll container
            padding: 4,
            margin: -4,
          }}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TendModal;
